import React, { useState } from "react";
import { Box, Typography, Button, Grid, Paper, styled } from "@mui/material";
import {
  TypewriterText,
  StaggeredText,
  BouncingText,
  ElasticText,
  WaveText,
  ShimmerText,
  GlowPulseText,
  FlipInText,
  RevealText,
  SplitText,
  useTextAnimation,
} from "./TextAnimations";

const ShowcaseContainer = styled(Box)(({ theme }) => ({
  padding: "80px 40px",
  backgroundColor: "#f8f9fa",
  minHeight: "100vh",
  [theme.breakpoints.down("md")]: {
    padding: "40px 20px",
  },
}));

const ShowcaseTitle = styled(Typography)(({ theme }) => ({
  textAlign: "center",
  fontFamily: "Inter",
  fontWeight: 700,
  fontSize: "48px",
  color: "#14582D",
  marginBottom: "60px",
  [theme.breakpoints.down("md")]: {
    fontSize: "32px",
    marginBottom: "40px",
  },
}));

const AnimationCard = styled(Paper)(({ theme }) => ({
  padding: "40px",
  borderRadius: "20px",
  backgroundColor: "#ffffff",
  boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
  height: "300px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  textAlign: "center",
  transition: "all 0.3s ease",
  border: "2px solid transparent",
  "&:hover": {
    transform: "translateY(-5px)",
    boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
    borderColor: "#29BC62",
  },
  [theme.breakpoints.down("md")]: {
    padding: "20px",
    height: "250px",
  },
}));

const AnimationLabel = styled(Typography)(({ theme }) => ({
  fontFamily: "Inter",
  fontWeight: 600,
  fontSize: "18px",
  color: "#29BC62",
  marginBottom: "20px",
  textTransform: "uppercase",
  letterSpacing: "1px",
}));

const AnimationText = styled(Typography)(({ theme }) => ({
  fontFamily: "Inter",
  fontWeight: 500,
  fontSize: "24px",
  color: "#14582D",
  lineHeight: "1.4",
  [theme.breakpoints.down("md")]: {
    fontSize: "20px",
  },
}));

const ControlButton = styled(Button)(({ theme }) => ({
  margin: "10px",
  padding: "12px 24px",
  borderRadius: "25px",
  fontWeight: 600,
  textTransform: "none",
  background: "linear-gradient(45deg, #29BC62, #F5933D)",
  color: "#FFFFFF",
  "&:hover": {
    background: "linear-gradient(45deg, #F5933D, #29BC62)",
    transform: "scale(1.05)",
  },
  transition: "all 0.3s ease",
}));

const TextAnimationShowcase: React.FC = () => {
  const [triggerAnimations, setTriggerAnimations] = useState(true);
  const { elementRef, isVisible } = useTextAnimation(0.1);

  const handleReplay = () => {
    setTriggerAnimations(false);
    setTimeout(() => setTriggerAnimations(true), 100);
  };

  const sampleText = "Delightful Text Animation";
  const shortText = "Amazing!";

  const animationExamples = [
    {
      name: "Typewriter Effect",
      component: (
        <TypewriterText
          text={sampleText}
          speed={100}
          delay={0.5}
          trigger={triggerAnimations && isVisible}
          component={AnimationText}
        />
      ),
    },
    {
      name: "Staggered Words",
      component: (
        <StaggeredText
          text={sampleText}
          staggerBy="words"
          staggerDelay={0.2}
          delay={0.3}
          trigger={triggerAnimations && isVisible}
          component={AnimationText}
        />
      ),
    },
    {
      name: "Staggered Letters",
      component: (
        <StaggeredText
          text={shortText}
          staggerBy="letters"
          staggerDelay={0.1}
          delay={0.3}
          trigger={triggerAnimations && isVisible}
          component={AnimationText}
        />
      ),
    },
    {
      name: "Bouncing Text",
      component: (
        <BouncingText
          delay={0.5}
          trigger={triggerAnimations && isVisible}
          component={AnimationText}
        >
          {shortText}
        </BouncingText>
      ),
    },
    {
      name: "Elastic Effect",
      component: (
        <ElasticText
          delay={0.5}
          trigger={triggerAnimations && isVisible}
          component={AnimationText}
        >
          {shortText}
        </ElasticText>
      ),
    },
    {
      name: "Wave Animation",
      component: (
        <WaveText
          text="Wave Effect!"
          waveDelay={0.1}
          delay={0.5}
          trigger={triggerAnimations && isVisible}
          component={AnimationText}
        />
      ),
    },
    {
      name: "Shimmer Gradient",
      component: (
        <ShimmerText
          delay={0.5}
          trigger={triggerAnimations && isVisible}
          component={AnimationText}
        >
          {sampleText}
        </ShimmerText>
      ),
    },
    {
      name: "Glow Pulse",
      component: (
        <GlowPulseText
          delay={0.5}
          trigger={triggerAnimations && isVisible}
          component={AnimationText}
        >
          {shortText}
        </GlowPulseText>
      ),
    },
    {
      name: "Flip In",
      component: (
        <FlipInText
          delay={0.5}
          trigger={triggerAnimations && isVisible}
          component={AnimationText}
        >
          {shortText}
        </FlipInText>
      ),
    },
    {
      name: "Reveal Effect",
      component: (
        <RevealText
          text={sampleText}
          delay={0.5}
          trigger={triggerAnimations && isVisible}
          component={AnimationText}
        />
      ),
    },
    {
      name: "Split Slide Up",
      component: (
        <SplitText
          text="Split Animation"
          splitBy="words"
          animationType="slideUp"
          staggerDelay={0.2}
          delay={0.3}
          trigger={triggerAnimations && isVisible}
          component={AnimationText}
        />
      ),
    },
    {
      name: "Split Rotate",
      component: (
        <SplitText
          text="Rotate Split"
          splitBy="letters"
          animationType="rotate"
          staggerDelay={0.1}
          delay={0.3}
          trigger={triggerAnimations && isVisible}
          component={AnimationText}
        />
      ),
    },
  ];

  return (
    <ShowcaseContainer ref={elementRef}>
      <ShowcaseTitle>
        Text Animation Showcase
      </ShowcaseTitle>

      <Box sx={{ textAlign: "center", marginBottom: "40px" }}>
        <ControlButton onClick={handleReplay}>
          Replay All Animations
        </ControlButton>
      </Box>

      <Grid container spacing={4}>
        {animationExamples.map((example, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <AnimationCard>
              <AnimationLabel>{example.name}</AnimationLabel>
              <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                {example.component}
              </Box>
            </AnimationCard>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ textAlign: "center", marginTop: "60px" }}>
        <Typography variant="h5" sx={{ fontFamily: "Inter", fontWeight: 600, color: "#14582D", marginBottom: "20px" }}>
          Implementation Examples
        </Typography>
        <Typography variant="body1" sx={{ fontFamily: "Inter", color: "#5a5d63", maxWidth: "800px", margin: "0 auto" }}>
          These animations can be easily integrated into any section of your homepage. Each animation is optimized for performance
          with CSS-based animations and includes accessibility features like reduced-motion support. Mix and match different
          animation types to create engaging user experiences that delight your visitors.
        </Typography>
      </Box>
    </ShowcaseContainer>
  );
};

export default TextAnimationShowcase;